import moment from 'moment';

import styled from 'styled-components';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { AnimatedDashboard, ErrorToast } from '../../components';

import Spinner from '../../components/common/Spinner';
import { akashaCoins, formatUSD, USDT } from '../../helpers/helperFunctions';

import { CaptionText, H3, P1 } from '../../styles/FontStyles';
import {
  Button,
  Flexed,
  SectionSeparator
} from '../../styles/GlobalComponents';
import { color, colors } from '../../styles/GlobalStyles';
import { BoxPortfolio, SummaryGrid } from './Portfolio.styled';
import { DepositUlx } from './UlxDeposit/UlxDeposit';

import { Link, useParams } from 'react-router-dom';

export const AkashaButton = styled(Button)<{ selected: boolean }>`
  color: ${({ theme }) => theme.colors.neutral900};
  background: ${({ selected }) => (selected ? '#01f1c1' : 'transparent')};
  font-size: 24px;
  height: 60px;
  @media (max-width: 800px) {
    font-size: 18px;
  }
`;

const SummaryGridAkasha = styled(SummaryGrid)`
  grid-template-columns: repeat(4, 1fr);
  @media (max-width: 1100px) {
    grid-template-columns: repeat(2, 1fr);
  }
  @media (max-width: 800px) {
    grid-template-columns: repeat(1, 1fr);
  }
  margin: 40px 0;
`;

const AkashaOn = styled.div`
  position: relative;
  width: 100%;

  svg {
    position: absolute;
    left: 50%;
    bottom: -36px;
    transform: translate(-50%, 0);
  }
`;

const AkashaButtonsWrapper = styled.div`
  width: 100%;
  display: flex;
  box-shadow: 0px 0.67px 0.67px 0px #ffffff inset,
    0px -0.67px 0.53px 0px #00000040 inset, 0px 1.34px 2.37px 0px #00000059,
    0px 0.67px 0.67px 0px #26c8c4;
  border-radius: 100px;
  background: #ffffff;
`;

const AkashaOff = styled.div`
  position: relative;
  width: 100%;

  svg {
    position: absolute;
    top: -24px;
    right: 0px;
  }
`;

export const ManageAkashaNode = () => {
  const [ulx, setUlx] = useState('');
  const [usdt, setUsdt] = useState('');

  const dispatch = useAppDispatch();
  const { loadingSummary } = useAppSelector((state) => state.portfolio);

  const [depositStep, setDepositStep] = useState(
    DepositStepsEnum.COMPLETE_DEPOSIT
  );
  const { userPackage, walletSummary } = useAppSelector(
    (state) => state.marketplace
  );

  const { id } = useParams<{ id: string }>();

  const [selectedToken, setSelectedToken] = useState<TokensType>(
    akashaCoins[0]
  );

  const [nodeOdOff, setNodeOdOff] = useState(true);

  useEffect(() => {
    dispatch(getDepositAkashaInfo({ userPackageId: id }));
  }, [dispatch]);

  return (
    <>
      <AnimatedDashboard zoom dark>
        <div style={{ marginBottom: 40 }}>
          <Flexed between mb='40' center>
            <H3 color={color.colors.primary900}>My Guardian Node</H3>
            <Link to={AKASHA}>
              <Button w='300px' wSM='100%'>
                Buy Guardian Nodes
              </Button>
            </Link>
          </Flexed>
          <ManageAkashaItem item={userPackage?.package as any} />

          <SummaryGridAkasha>
            <BoxPortfolio>
              <div>
                <CaptionText caps bold color={color.colors.neutral500} mb='10'>
                  NODE ID NUMBER
                </CaptionText>

                <P1 bold flex alignEnd gap='5px'>
                  {userPackage?.id}
                </P1>
              </div>
            </BoxPortfolio>
            <BoxPortfolio>
              <div>
                <CaptionText caps bold color={color.colors.neutral500} mb='10'>
                  NODE PURCHASED DATE
                </CaptionText>

                <P1 bold flex alignEnd gap='5px'>
                  {moment(userPackage?.datePaid).format('DD MMM YYYY')}
                </P1>
              </div>
            </BoxPortfolio>

            <BoxPortfolio>
              <div>
                <CaptionText caps bold color={color.colors.neutral500} mb='10'>
                  CURRENT AMOUNT DEPOSITED
                </CaptionText>

                <P1 bold flex alignEnd gap='5px'>
                  {formatUSD(userPackage?.depositedAmount)} {USDT}
                </P1>
              </div>
            </BoxPortfolio>

            <BoxPortfolio>
              <div>
                <CaptionText caps bold color={color.colors.neutral500} mb='10'>
                  TOTAL AMOUNT MINTED
                </CaptionText>

                <P1 bold flex alignEnd gap='5px'>
                  {formatUSD(userPackage?.mintedAmount)}{' '}
                  {userPackage?.mintedTokenCurrency}
                </P1>
              </div>
            </BoxPortfolio>
          </SummaryGridAkasha>

          <div style={{ position: 'relative' }}>
            {loadingSummary && <Spinner absolute transparent />}
          </div>
          <P1 color={colors.neutral0} bold>
            Select Coin
          </P1>
          <Flexed gap='200px' gapSM='30px' center>
            <Flexed width='50%'>
              <SelectAkashaToken
                onChange={(token) => setSelectedToken(token)}
                value={selectedToken}
              />
            </Flexed>
            <Flexed width='50%' mb='20'>
              <AkashaButtonsWrapper>
                <AkashaOn>
                  <AkashaButton
                    selected={nodeOdOff}
                    maxWidth='350px'
                    maxWidthSM='100%'>
                    Node ON
                  </AkashaButton>
                  <PreferredOptionSvg />
                </AkashaOn>
                <AkashaOff>
                  <AkashaButton
                    selected={!nodeOdOff}
                    maxWidth='350px'
                    maxWidthSM='100%'
                    disabled>
                    Node OFF
                  </AkashaButton>
                  <ComingSoonSvg />
                </AkashaOff>
              </AkashaButtonsWrapper>
            </Flexed>
          </Flexed>

          <Flexed center justifyCenter>
            <Button
              m='50px 0'
              maxWidth='350px'
              maxWidthSM='100%'
              onClick={() => {
                if (
                  selectedToken.name !== userPackage?.mintedTokenCurrency &&
                  userPackage?.depositedAmount !== 0
                ) {
                  ErrorToast(
                    'Deposit with a different token type is not allowed. Use the same token as your previous deposit.'
                  );
                } else {
                  dispatch(getAkashaWalletSummary());
                  setDepositStep(DepositStepsEnum.COMPLETE_DEPOSIT);
                  dispatch(toggleDepositUlxModal(true));
                  setUlx('');
                  setUsdt('');
                }
              }}>
              Complete{' '}
              {selectedToken.token === 'Ultron (ULX)' ? 'ULX' : 'AK1111'}{' '}
              Deposit
            </Button>
          </Flexed>
        </div>
        <SectionSeparator />
        <H3 color={colors.primary900} mb='24'>
          Super Node Status: ON vs OFF – What It Means
        </H3>
        <P1 color={colors.neutral0}>
          When you activate your Super Node and set it to <b>ON</b>, you are
          enabling <b>Transaction Mining</b> within the Super Node Network. This
          means your node will actively participate in the ecosystem’s
          operations and share in the profits generated from{' '}
          <b>50% of Super Node’s transaction fees.</b>
          <br />
          <br />
          <ul>
            <li>
              The coin you use to deposit into your node — either <b>ULX</b> or{' '}
              <b>AK1111</b> — will also be the <b>coin your node mines.</b>
            </li>
            <li>
              Your deposited amount will begin generating <b>monthly rewards</b>{' '}
              based on your node tier.
            </li>
            <li>
              These earnings are <b>compiled monthly</b> and continue to accrue
              for a<b>10-year period.</b>
            </li>
            <li>
              At the end of each year (based on the anniversary of your initial
              deposit), you may <b>withdraw up to 10%</b> of your total
              accumulated rewards.
            </li>
            <li>
              Please note: The <b>initial deposit</b> into your node is{' '}
              <b>locked for the full 10-year mining period</b> and cannot be
              withdrawn until that time has passed.
            </li>
          </ul>
          <br />
          When your node is set to <b>OFF</b>, it is not participating in
          Transaction Mining and does{' '}
          <b>not earn a share of Super Node’s transaction fees.</b>
          <br />
          <br />
          <ul>
            <li>
              Instead, your node functions in a passive state and earns a{' '}
              <b>flat monthly percentage return</b> on your deposited amount.
            </li>
            <li>
              Unlike the ON setting, these earnings are <b>not compiled</b>{' '}
              long-term and are{' '}
              <b>available for withdrawal at the end of each month.</b>
            </li>
            <li>
              Your node does not <b>accumulate mining rewards</b> while OFF.
            </li>
          </ul>
        </P1>
      </AnimatedDashboard>
      <DepositUlx
        token={selectedToken}
        depositStep={depositStep}
        setDepositStep={setDepositStep}
        ulx={ulx}
        usdt={usdt}
        setUlx={setUlx}
        setUsdt={setUsdt}
      />
    </>
  );
};

import { H4, P2, P3 } from '../../styles/FontStyles';
import { BoxBig, HubImage, Td, Tr } from './Portfolio.styled';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toggleDepositUlxModal } from '../../slices/portfolio-slice';
import { TokensType, WalletsType } from '../../types/types';
import { Circle } from '../Ranking/RankingStyled';
import { PackageTrading } from './Portfolio.types';
import { SelectAkashaToken } from '../Marketplace/Akasha/SelectAkashaToken';
import {
  getDepositAkashaInfo,
  getAkashaWalletSummary
} from '../Marketplace/Marketplace.actions';
import { DepositStepsEnum } from './UlxDeposit/types';
import { AKASHA } from '../../routes/routes';

export const ManageAkashaItem = ({ item }: { item: PackageTrading }) => {
  const { t } = useTranslation('my_hubs');

  const { userPackage } = useAppSelector((state) => state.marketplace);

  return (
    <>
      <BoxBig relative>
        <HubImage image={item?.image} />

        <div>
          <Flexed center between>
            <H3>{item?.title}</H3>
            <Flexed center end>
              <P3 bold caps>
                {userPackage?.isActive ? t('active') : t('inactive')}
              </P3>

              <Circle
                color={
                  userPackage?.isActive
                    ? color.colors.success500
                    : color.colors.error500
                }
              />
            </Flexed>
          </Flexed>
          <div>
            <div
              style={{
                position: 'absolute',
                left: '50%',
                right: '50%',
                transform: 'translate(-50%, -50%)'
              }}>
              <Spinner />
            </div>

            <H4 mb='12'>{item?.name}</H4>
            <P2 mb='32'>{item?.subtitle}</P2>

            <Tr style={{ cursor: 'pointer' }}>
              <Td w='20%' wSM='50%'>
                <CaptionText caps bold color={color.colors.neutral500} mb='10'>
                  Max Deposit Amount
                </CaptionText>
                <P1 bold flex alignEnd gap='5px'>
                  {formatUSD(item?.maxDepositAmount ?? 0)} <P3 mb='3'>USDT</P3>
                </P1>
              </Td>

              <Td w='20%' wSM='50%'>
                <CaptionText caps bold color={color.colors.neutral500} mb='10'>
                  Node on
                </CaptionText>
                <P1 bold>{item?.nodeOnPercentage}%</P1>
              </Td>

              <Td w='20%' wSM='50%'>
                <CaptionText caps bold color={color.colors.neutral500} mb='10'>
                  Node off
                </CaptionText>
                <P1 bold>{item?.nodeOffPercentage}%</P1>
              </Td>
            </Tr>
          </div>
          {/* 
          <Flexed end>
            <Button condensed mt='20' w='auto' p='0 16px' disabled>
              Activate Transaction Mining
            </Button>
          </Flexed> */}
        </div>
      </BoxBig>
    </>
  );
};

const ComingSoonSvg = () => (
  <svg
    width='132'
    height='33'
    viewBox='0 0 132 33'
    fill='none'
    xmlns='http://www.w3.org/2000/svg'>
    <path
      d='M1.07274 28.3515L1.07276 28.3514L5.04922 20.4375L5.04923 20.4374C6.19372 18.1592 6.19375 15.342 5.04921 13.0644L1.07277 5.15115C1.07277 5.15113 1.07276 5.15112 1.07275 5.15111C0.598193 4.2064 0.65196 3.13607 1.05999 2.30362C1.46812 1.47094 2.2068 0.914588 3.08611 0.914588L128.569 0.915822C129.448 0.915822 130.187 1.47222 130.595 2.30485C131.003 3.13729 131.057 4.2076 130.582 5.15231L130.89 5.30692L130.582 5.15235L126.606 13.0663L126.913 13.2209L126.606 13.0663C125.461 15.3446 125.461 18.1618 126.606 20.4394L130.582 28.3527C131.057 29.2973 131.003 30.3677 130.595 31.2002C130.187 32.0328 129.448 32.5892 128.569 32.5892L3.08612 32.5879C2.20647 32.5879 1.46767 32.0316 1.05956 31.1989C0.651543 30.3665 0.597902 29.2962 1.07274 28.3515Z'
      fill='#00FFF2'
      stroke='#71FFE3'
      strokeWidth='0.688551'
    />
    <path
      d='M31.8683 21.0788C31.1677 21.0788 30.5326 20.9282 29.963 20.627C29.3999 20.3193 28.9547 19.897 28.6273 19.3601C28.3065 18.8167 28.1461 18.2045 28.1461 17.5236C28.1461 16.8426 28.3065 16.2337 28.6273 15.6969C28.9547 15.1534 29.3999 14.7311 29.963 14.4299C30.5326 14.1222 31.171 13.9683 31.8781 13.9683C32.4739 13.9683 33.0108 14.0731 33.4887 14.2826C33.9732 14.4921 34.3792 14.7933 34.7065 15.1862L33.6852 16.129C33.2203 15.5921 32.6441 15.3237 31.9567 15.3237C31.5311 15.3237 31.1513 15.4186 30.8174 15.6085C30.4835 15.7918 30.2216 16.0504 30.0317 16.3843C29.8484 16.7182 29.7567 17.098 29.7567 17.5236C29.7567 17.9491 29.8484 18.3289 30.0317 18.6628C30.2216 18.9967 30.4835 19.2586 30.8174 19.4485C31.1513 19.6318 31.5311 19.7235 31.9567 19.7235C32.6441 19.7235 33.2203 19.4518 33.6852 18.9083L34.7065 19.8512C34.3792 20.2505 33.9732 20.555 33.4887 20.7645C33.0042 20.974 32.4641 21.0788 31.8683 21.0788ZM38.9664 21.0788C38.2528 21.0788 37.6079 20.9249 37.0317 20.6172C36.4621 20.3095 36.0136 19.8872 35.6862 19.3503C35.3654 18.8069 35.205 18.1979 35.205 17.5236C35.205 16.8492 35.3654 16.2436 35.6862 15.7067C36.0136 15.1632 36.4621 14.7377 37.0317 14.4299C37.6079 14.1222 38.2528 13.9683 38.9664 13.9683C39.6801 13.9683 40.3217 14.1222 40.8914 14.4299C41.461 14.7377 41.9095 15.1632 42.2368 15.7067C42.5642 16.2436 42.7279 16.8492 42.7279 17.5236C42.7279 18.1979 42.5642 18.8069 42.2368 19.3503C41.9095 19.8872 41.461 20.3095 40.8914 20.6172C40.3217 20.9249 39.6801 21.0788 38.9664 21.0788ZM38.9664 19.7235C39.3724 19.7235 39.739 19.6318 40.0664 19.4485C40.3938 19.2586 40.6491 18.9967 40.8324 18.6628C41.0223 18.3289 41.1172 17.9491 41.1172 17.5236C41.1172 17.098 41.0223 16.7182 40.8324 16.3843C40.6491 16.0504 40.3938 15.7918 40.0664 15.6085C39.739 15.4186 39.3724 15.3237 38.9664 15.3237C38.5605 15.3237 38.1938 15.4186 37.8665 15.6085C37.5391 15.7918 37.2805 16.0504 37.0906 16.3843C36.9073 16.7182 36.8156 17.098 36.8156 17.5236C36.8156 17.9491 36.9073 18.3289 37.0906 18.6628C37.2805 18.9967 37.5391 19.2586 37.8665 19.4485C38.1938 19.6318 38.5605 19.7235 38.9664 19.7235ZM50.1796 20.9609L50.1698 16.8361L48.1467 20.2342H47.4297L45.4164 16.9245V20.9609H43.9236V14.0862H45.2396L47.8127 18.3584L50.3466 14.0862H51.6528L51.6724 20.9609H50.1796ZM53.3035 14.0862H54.8945V20.9609H53.3035V14.0862ZM62.8311 14.0862V20.9609H61.5249L58.0974 16.787V20.9609H56.526V14.0862H57.842L61.2598 18.2601V14.0862H62.8311ZM69.103 17.4155H70.5565V20.2047C70.1833 20.4863 69.7512 20.7023 69.2602 20.8529C68.7691 21.0035 68.2748 21.0788 67.7772 21.0788C67.0635 21.0788 66.4219 20.9282 65.8523 20.627C65.2826 20.3193 64.8341 19.897 64.5068 19.3601C64.186 18.8167 64.0255 18.2045 64.0255 17.5236C64.0255 16.8426 64.186 16.2337 64.5068 15.6969C64.8341 15.1534 65.2859 14.7311 65.8621 14.4299C66.4382 14.1222 67.0864 13.9683 67.8066 13.9683C68.409 13.9683 68.9557 14.0698 69.4468 14.2728C69.9378 14.4758 70.3503 14.7704 70.6842 15.1567L69.6628 16.0995C69.1718 15.5823 68.5792 15.3237 67.8852 15.3237C67.4465 15.3237 67.057 15.4153 66.7165 15.5986C66.376 15.782 66.1109 16.0406 65.921 16.3745C65.7311 16.7084 65.6362 17.0914 65.6362 17.5236C65.6362 17.9491 65.7311 18.3289 65.921 18.6628C66.1109 18.9967 66.3728 19.2586 66.7067 19.4485C67.0472 19.6318 67.4334 19.7235 67.8656 19.7235C68.3239 19.7235 68.7364 19.6253 69.103 19.4289V17.4155ZM77.0747 21.0788C76.5312 21.0788 76.0042 21.0068 75.4935 20.8627C74.9893 20.7121 74.5834 20.519 74.2757 20.2833L74.8158 19.0851C75.1104 19.3012 75.4607 19.4747 75.8667 19.6056C76.2726 19.7366 76.6785 19.8021 77.0845 19.8021C77.5362 19.8021 77.8702 19.7366 78.0862 19.6056C78.3023 19.4681 78.4103 19.2881 78.4103 19.0655C78.4103 18.9018 78.3448 18.7676 78.2139 18.6628C78.0895 18.5515 77.9258 18.4631 77.7228 18.3976C77.5264 18.3322 77.258 18.2601 76.9175 18.1816C76.3937 18.0572 75.9649 17.9328 75.631 17.8084C75.297 17.684 75.009 17.4843 74.7667 17.2093C74.531 16.9343 74.4132 16.5677 74.4132 16.1093C74.4132 15.7099 74.5212 15.3498 74.7372 15.029C74.9533 14.7017 75.2774 14.443 75.7095 14.2532C76.1482 14.0633 76.6818 13.9683 77.3104 13.9683C77.749 13.9683 78.1779 14.0207 78.5969 14.1255C79.016 14.2302 79.3826 14.3808 79.6969 14.5773L79.2058 15.7852C78.5707 15.4251 77.9356 15.2451 77.3005 15.2451C76.8553 15.2451 76.5247 15.3171 76.3086 15.4611C76.0991 15.6052 75.9943 15.7951 75.9943 16.0308C75.9943 16.2665 76.1155 16.4433 76.3577 16.5611C76.6065 16.6724 76.983 16.7837 77.4871 16.895C78.0109 17.0194 78.4398 17.1438 78.7737 17.2682C79.1076 17.3926 79.3924 17.589 79.6281 17.8575C79.8704 18.1259 79.9915 18.4893 79.9915 18.9476C79.9915 19.3405 79.8802 19.7006 79.6576 20.0279C79.4415 20.3488 79.1142 20.6041 78.6755 20.794C78.2368 20.9839 77.7032 21.0788 77.0747 21.0788ZM84.408 21.0788C83.6943 21.0788 83.0494 20.9249 82.4732 20.6172C81.9036 20.3095 81.4551 19.8872 81.1278 19.3503C80.8069 18.8069 80.6465 18.1979 80.6465 17.5236C80.6465 16.8492 80.8069 16.2436 81.1278 15.7067C81.4551 15.1632 81.9036 14.7377 82.4732 14.4299C83.0494 14.1222 83.6943 13.9683 84.408 13.9683C85.1217 13.9683 85.7633 14.1222 86.3329 14.4299C86.9025 14.7377 87.351 15.1632 87.6784 15.7067C88.0058 16.2436 88.1695 16.8492 88.1695 17.5236C88.1695 18.1979 88.0058 18.8069 87.6784 19.3503C87.351 19.8872 86.9025 20.3095 86.3329 20.6172C85.7633 20.9249 85.1217 21.0788 84.408 21.0788ZM84.408 19.7235C84.8139 19.7235 85.1806 19.6318 85.508 19.4485C85.8353 19.2586 86.0907 18.9967 86.274 18.6628C86.4639 18.3289 86.5588 17.9491 86.5588 17.5236C86.5588 17.098 86.4639 16.7182 86.274 16.3843C86.0907 16.0504 85.8353 15.7918 85.508 15.6085C85.1806 15.4186 84.8139 15.3237 84.408 15.3237C84.0021 15.3237 83.6354 15.4186 83.308 15.6085C82.9807 15.7918 82.722 16.0504 82.5322 16.3843C82.3488 16.7182 82.2572 17.098 82.2572 17.5236C82.2572 17.9491 82.3488 18.3289 82.5322 18.6628C82.722 18.9967 82.9807 19.2586 83.308 19.4485C83.6354 19.6318 84.0021 19.7235 84.408 19.7235ZM92.6945 21.0788C91.9808 21.0788 91.3359 20.9249 90.7598 20.6172C90.1901 20.3095 89.7416 19.8872 89.4143 19.3503C89.0935 18.8069 88.933 18.1979 88.933 17.5236C88.933 16.8492 89.0935 16.2436 89.4143 15.7067C89.7416 15.1632 90.1901 14.7377 90.7598 14.4299C91.3359 14.1222 91.9808 13.9683 92.6945 13.9683C93.4082 13.9683 94.0498 14.1222 94.6194 14.4299C95.1891 14.7377 95.6376 15.1632 95.9649 15.7067C96.2923 16.2436 96.456 16.8492 96.456 17.5236C96.456 18.1979 96.2923 18.8069 95.9649 19.3503C95.6376 19.8872 95.1891 20.3095 94.6194 20.6172C94.0498 20.9249 93.4082 21.0788 92.6945 21.0788ZM92.6945 19.7235C93.1004 19.7235 93.4671 19.6318 93.7945 19.4485C94.1218 19.2586 94.3772 18.9967 94.5605 18.6628C94.7504 18.3289 94.8453 17.9491 94.8453 17.5236C94.8453 17.098 94.7504 16.7182 94.5605 16.3843C94.3772 16.0504 94.1218 15.7918 93.7945 15.6085C93.4671 15.4186 93.1004 15.3237 92.6945 15.3237C92.2886 15.3237 91.9219 15.4186 91.5945 15.6085C91.2672 15.7918 91.0086 16.0504 90.8187 16.3843C90.6354 16.7182 90.5437 17.098 90.5437 17.5236C90.5437 17.9491 90.6354 18.3289 90.8187 18.6628C91.0086 18.9967 91.2672 19.2586 91.5945 19.4485C91.9219 19.6318 92.2886 19.7235 92.6945 19.7235ZM103.957 14.0862V20.9609H102.651L99.2231 16.787V20.9609H97.6517V14.0862H98.9677L102.385 18.2601V14.0862H103.957Z'
      fill='black'
    />
  </svg>
);

const PreferredOptionSvg = () => (
  <svg
    width='132'
    height='26'
    viewBox='0 0 132 26'
    fill='none'
    xmlns='http://www.w3.org/2000/svg'>
    <path
      d='M13.144 10.5042C13.7529 10.5042 14.28 10.6057 14.7252 10.8086C15.177 11.0116 15.524 11.2997 15.7663 11.6729C16.0085 12.0461 16.1296 12.488 16.1296 12.9987C16.1296 13.5029 16.0085 13.9448 15.7663 14.3246C15.524 14.6978 15.177 14.9858 14.7252 15.1888C14.28 15.3852 13.7529 15.4834 13.144 15.4834H11.7593V17.3789H10.1683V10.5042H13.144ZM13.0556 14.1871C13.5336 14.1871 13.897 14.0856 14.1458 13.8826C14.3946 13.6731 14.519 13.3785 14.519 12.9987C14.519 12.6124 14.3946 12.3178 14.1458 12.1148C13.897 11.9053 13.5336 11.8005 13.0556 11.8005H11.7593V14.1871H13.0556ZM22.0385 17.3789L20.7127 15.4638H20.6341H19.2494V17.3789H17.6583V10.5042H20.6341C21.243 10.5042 21.7701 10.6057 22.2153 10.8086C22.6671 11.0116 23.0141 11.2997 23.2563 11.6729C23.4986 12.0461 23.6197 12.488 23.6197 12.9987C23.6197 13.5094 23.4953 13.9514 23.2465 14.3246C23.0043 14.6912 22.6573 14.9727 22.2055 15.1692L23.7474 17.3789H22.0385ZM22.0091 12.9987C22.0091 12.6124 21.8847 12.3178 21.6359 12.1148C21.3871 11.9053 21.0237 11.8005 20.5457 11.8005H19.2494V14.1969H20.5457C21.0237 14.1969 21.3871 14.0921 21.6359 13.8826C21.8847 13.6731 22.0091 13.3785 22.0091 12.9987ZM30.5961 16.1022V17.3789H25.2731V10.5042H30.4685V11.7809H26.8543V13.2737H30.0461V14.5112H26.8543V16.1022H30.5961ZM33.8459 11.7809V13.5978H37.0279V14.8745H33.8459V17.3789H32.2549V10.5042H37.4502V11.7809H33.8459ZM44.2432 16.1022V17.3789H38.9202V10.5042H44.1155V11.7809H40.5013V13.2737H43.6932V14.5112H40.5013V16.1022H44.2432ZM50.2821 17.3789L48.9563 15.4638H48.8777H47.4929V17.3789H45.9019V10.5042H48.8777C49.4866 10.5042 50.0137 10.6057 50.4589 10.8086C50.9107 11.0116 51.2577 11.2997 51.4999 11.6729C51.7422 12.0461 51.8633 12.488 51.8633 12.9987C51.8633 13.5094 51.7389 13.9514 51.4901 14.3246C51.2479 14.6912 50.9008 14.9727 50.4491 15.1692L51.991 17.3789H50.2821ZM50.2527 12.9987C50.2527 12.6124 50.1283 12.3178 49.8795 12.1148C49.6307 11.9053 49.2673 11.8005 48.7893 11.8005H47.4929V14.1969H48.7893C49.2673 14.1969 49.6307 14.0921 49.8795 13.8826C50.1283 13.6731 50.2527 13.3785 50.2527 12.9987ZM57.8969 17.3789L56.571 15.4638H56.4925H55.1077V17.3789H53.5167V10.5042H56.4925C57.1014 10.5042 57.6284 10.6057 58.0737 10.8086C58.5254 11.0116 58.8725 11.2997 59.1147 11.6729C59.357 12.0461 59.4781 12.488 59.4781 12.9987C59.4781 13.5094 59.3537 13.9514 59.1049 14.3246C58.8626 14.6912 58.5156 14.9727 58.0638 15.1692L59.6058 17.3789H57.8969ZM57.8674 12.9987C57.8674 12.6124 57.743 12.3178 57.4942 12.1148C57.2454 11.9053 56.882 11.8005 56.4041 11.8005H55.1077V14.1969H56.4041C56.882 14.1969 57.2454 14.0921 57.4942 13.8826C57.743 13.6731 57.8674 13.3785 57.8674 12.9987ZM66.4545 16.1022V17.3789H61.1315V10.5042H66.3268V11.7809H62.7127V13.2737H65.9045V14.5112H62.7127V16.1022H66.4545ZM68.1132 10.5042H71.2363C71.9827 10.5042 72.6408 10.6482 73.2104 10.9363C73.7865 11.2178 74.2318 11.6172 74.546 12.1345C74.8669 12.6517 75.0273 13.2541 75.0273 13.9415C75.0273 14.629 74.8669 15.2314 74.546 15.7486C74.2318 16.2659 73.7865 16.6685 73.2104 16.9566C72.6408 17.2381 71.9827 17.3789 71.2363 17.3789H68.1132V10.5042ZM71.1578 16.0727C71.8452 16.0727 72.392 15.8828 72.7979 15.5031C73.2104 15.1168 73.4166 14.5963 73.4166 13.9415C73.4166 13.2868 73.2104 12.7696 72.7979 12.3898C72.392 12.0035 71.8452 11.8104 71.1578 11.8104H69.7043V16.0727H71.1578ZM83.1235 17.4968C82.4098 17.4968 81.7649 17.3429 81.1888 17.0352C80.6191 16.7274 80.1706 16.3051 79.8433 15.7683C79.5225 15.2248 79.362 14.6159 79.362 13.9415C79.362 13.2672 79.5225 12.6615 79.8433 12.1246C80.1706 11.5812 80.6191 11.1556 81.1888 10.8479C81.7649 10.5402 82.4098 10.3863 83.1235 10.3863C83.8372 10.3863 84.4788 10.5402 85.0484 10.8479C85.6181 11.1556 86.0665 11.5812 86.3939 12.1246C86.7213 12.6615 86.885 13.2672 86.885 13.9415C86.885 14.6159 86.7213 15.2248 86.3939 15.7683C86.0665 16.3051 85.6181 16.7274 85.0484 17.0352C84.4788 17.3429 83.8372 17.4968 83.1235 17.4968ZM83.1235 16.1415C83.5294 16.1415 83.8961 16.0498 84.2235 15.8665C84.5508 15.6766 84.8062 15.4147 84.9895 15.0808C85.1794 14.7469 85.2743 14.3671 85.2743 13.9415C85.2743 13.516 85.1794 13.1362 84.9895 12.8023C84.8062 12.4684 84.5508 12.2098 84.2235 12.0264C83.8961 11.8366 83.5294 11.7416 83.1235 11.7416C82.7176 11.7416 82.3509 11.8366 82.0235 12.0264C81.6962 12.2098 81.4376 12.4684 81.2477 12.8023C81.0644 13.1362 80.9727 13.516 80.9727 13.9415C80.9727 14.3671 81.0644 14.7469 81.2477 15.0808C81.4376 15.4147 81.6962 15.6766 82.0235 15.8665C82.3509 16.0498 82.7176 16.1415 83.1235 16.1415ZM91.4493 10.5042C92.0582 10.5042 92.5853 10.6057 93.0305 10.8086C93.4823 11.0116 93.8293 11.2997 94.0715 11.6729C94.3138 12.0461 94.4349 12.488 94.4349 12.9987C94.4349 13.5029 94.3138 13.9448 94.0715 14.3246C93.8293 14.6978 93.4823 14.9858 93.0305 15.1888C92.5853 15.3852 92.0582 15.4834 91.4493 15.4834H90.0645V17.3789H88.4735V10.5042H91.4493ZM91.3609 14.1871C91.8389 14.1871 92.2022 14.0856 92.451 13.8826C92.6998 13.6731 92.8242 13.3785 92.8242 12.9987C92.8242 12.6124 92.6998 12.3178 92.451 12.1148C92.2022 11.9053 91.8389 11.8005 91.3609 11.8005H90.0645V14.1871H91.3609ZM97.1958 11.8005H94.9959V10.5042H100.987V11.8005H98.7869V17.3789H97.1958V11.8005ZM102.236 10.5042H103.827V17.3789H102.236V10.5042ZM109.18 17.4968C108.467 17.4968 107.822 17.3429 107.246 17.0352C106.676 16.7274 106.228 16.3051 105.9 15.7683C105.579 15.2248 105.419 14.6159 105.419 13.9415C105.419 13.2672 105.579 12.6615 105.9 12.1246C106.228 11.5812 106.676 11.1556 107.246 10.8479C107.822 10.5402 108.467 10.3863 109.18 10.3863C109.894 10.3863 110.536 10.5402 111.105 10.8479C111.675 11.1556 112.123 11.5812 112.451 12.1246C112.778 12.6615 112.942 13.2672 112.942 13.9415C112.942 14.6159 112.778 15.2248 112.451 15.7683C112.123 16.3051 111.675 16.7274 111.105 17.0352C110.536 17.3429 109.894 17.4968 109.18 17.4968ZM109.18 16.1415C109.586 16.1415 109.953 16.0498 110.28 15.8665C110.608 15.6766 110.863 15.4147 111.046 15.0808C111.236 14.7469 111.331 14.3671 111.331 13.9415C111.331 13.516 111.236 13.1362 111.046 12.8023C110.863 12.4684 110.608 12.2098 110.28 12.0264C109.953 11.8366 109.586 11.7416 109.18 11.7416C108.774 11.7416 108.408 11.8366 108.08 12.0264C107.753 12.2098 107.494 12.4684 107.305 12.8023C107.121 13.1362 107.03 13.516 107.03 13.9415C107.03 14.3671 107.121 14.7469 107.305 15.0808C107.494 15.4147 107.753 15.6766 108.08 15.8665C108.408 16.0498 108.774 16.1415 109.18 16.1415ZM120.836 10.5042V17.3789H119.529L116.102 13.205V17.3789H114.53V10.5042H115.846L119.264 14.6781V10.5042H120.836Z'
      fill='url(#paint0_linear_20930_14893)'
    />
    <rect
      x='0.751771'
      y='1.5213'
      width='129.692'
      height='23.6982'
      rx='11.8491'
      stroke='url(#paint1_linear_20930_14893)'
      strokeWidth='1.33167'
    />
    <defs>
      <linearGradient
        id='paint0_linear_20930_14893'
        x1='-0.781853'
        y1='4.59629'
        x2='113.03'
        y2='56.0279'
        gradientUnits='userSpaceOnUse'>
        <stop offset='0.00905331' stopColor='#00D3A9' />
        <stop offset='0.383557' stopColor='#007E65' />
        <stop offset='0.563708' stopColor='#084438' />
        <stop offset='0.817388' stopColor='#00B08D' />
        <stop offset='1' stopColor='#001724' />
      </linearGradient>
      <linearGradient
        id='paint1_linear_20930_14893'
        x1='-11.2561'
        y1='-4.94858'
        x2='138.225'
        y2='32.6024'
        gradientUnits='userSpaceOnUse'>
        <stop offset='0.00905331' stopColor='#00D3A9' />
        <stop offset='0.383557' stopColor='#007E65' />
        <stop offset='0.563708' stopColor='#084438' />
        <stop offset='0.817388' stopColor='#00B08D' />
        <stop offset='1' stopColor='#001724' />
      </linearGradient>
    </defs>
  </svg>
);
function useMatch(arg0: string) {
  throw new Error('Function not implemented.');
}
